import { join } from 'lodash'
import api, {
  PaginateRequest, PaginateResponse, ResponseCallback
} from '../services/api.service'
import routeUtil from '../utils/route.util'
import { CategoryData } from './category.api'
import { ContactSiteData } from './contactSite.api'
import { ContactSiteItemData } from './contactSiteItem.api'
import { ItemType, ItemTypeData } from './ItemType.api'
import { MaterialPivotData } from './material.api'
import { PalletData } from './pallet.api'
import { PurchaseOrderItemData } from './purchaseOrderItem.api'
import { ShipOrderItemData } from './shipOrderItem.api'
import { TransferData } from './transfer.api'
import { UnitData } from './unit.api'
import { UserData } from './user.api'
import { ItemPrintableData, ItemPrintableName } from './ItemPrintable.api'

export interface ItemData {
  id: number
  subscriberId: number
  categoryId: number
  createdBy: number
  updatedBy: number
  deletedBy?: number
  code: string
  description: string
  value: number
  cpp: number
  qpc: number
  cpb: number
  netWeight: number
  grossWeight: number
  minimumStock: number
  deletedAt?: string
  createdAt: string
  updatedAt: string
  isDeleted: boolean
  isTraceable: boolean
  isCaseless: boolean
  stockQuantity: number
  hasActiveInventory: boolean
  customerPartNumbers?: number[]
  pallets?: PalletData[]
  category?: CategoryData
  material: MaterialPivotData
  contactSiteItem?: ContactSiteItemData
  purchaseOrderItem?: PurchaseOrderItemData
  shipOrderItem?: ShipOrderItemData
  contactSites?: ContactSiteData[]
  updatedUser?: UserData
  types?: ItemTypeData[]
  printables?: ItemPrintableData[]
}

type ItemPaginateResponse = PaginateResponse<ItemData[]>
export type ItemListAliveTransfersRequestStatus = 'inventory' | 'material'

export interface ItemSearchByCodeRequest {
  types: ItemType[]
}

export interface ItemCreateRequestContact {
  site: number
  partNumber?: string
}

export interface ItemCreateRequestMaterial {
  material: number
  quantity?: number
}

export interface ItemListAliveTransfersRequest {
  item?: number
  status?: ItemListAliveTransfersRequestStatus
  allItems?: boolean
}

export interface ItemExportAliveTransferItemsRequest
  extends ItemListAliveTransfersRequest {}

export interface ItemCreateRequest {
  code?: string
  category?: number
  types?: ItemType[]
  description?: string
  value?: number
  netWeight?: number
  grossWeight?: number
  ppc?: number
  cpp?: number
  qpc?: number
  safetyStock?: number
  labels?: ItemPrintableName[]
  materials?: ItemCreateRequestMaterial[]
  customers?: ItemCreateRequestContact[]
  vendors?: ItemCreateRequestContact[]
}

export interface ItemUpdateRequest {
  types?: ItemType[]
  description?: string
  value?: number
  netWeight?: number
  grossWeight?: number
  cpp?: number
  qpc?: number
  ppc?: number
  safetyStock?: number
  labels?: ItemPrintableName[]
  materials?: ItemCreateRequestMaterial[]
  contactsAdded?: ItemCreateRequestContact[]
  contactsDeleted?: ItemCreateRequestContact[]
}

interface MaterialDeltaData {
  itemId: string
  delta: number
}

const routes = {
  read: '/items/{id}',
  readUnit: '/items/{id}/unit',
  searchByCode: '/items/search/code',
  search: '/items/search',
  listAliveTransfers: '/items/transfers/alive',
  listMaterials: '/items/{id}/materials',
  exportAliveTransferItems: '/items/transfers/alive/items/export',
  paginate: '/items/paginate',
  delete: '/items/{id}',
  create: '/items',
  update: '/items/{id}',
  materialsDelta: '/items/{id}/materialsDelta'
}

export default {
  read (id: number, callback: ResponseCallback<ItemData>) {
    const url = routeUtil.replaceParams(routes.read, { id })
    api.get(url, {}, callback)
  },

  readUnit (id: number, callback: ResponseCallback<UnitData>) {
    const url = routeUtil.replaceParams(routes.readUnit, { id })
    api.get(url, {}, callback)
  },

  searchByCode (
    code: string,
    types: ItemType[],
    callback: ResponseCallback<ItemData[]>
  ) {
    const params = {
      code,
      types: join(types, ',')
    }
    api.get(routes.searchByCode, params, callback)
  },

  search (item: string, callback: ResponseCallback<ItemData[]>) {
    const params = { item }
    api.get(routes.search, params, callback)
  },

  listAliveTransfers (
    payload: ItemListAliveTransfersRequest,
    callback: ResponseCallback<TransferData[]>
  ) {
    api.post(routes.listAliveTransfers, payload, callback)
  },

  listMaterials (id: number, callback: ResponseCallback<ItemData[]>) {
    const url = routeUtil.replaceParams(routes.listMaterials, { id })
    api.get(url, {}, callback)
  },

  exportAliveTransferItems (
    payload: ItemExportAliveTransferItemsRequest,
    callback: ResponseCallback<Blob>
  ) {
    api.post(routes.exportAliveTransferItems, payload, callback)
  },

  paginate (
    params: PaginateRequest,
    callback: ResponseCallback<ItemPaginateResponse>
  ) {
    api.get(routes.paginate, params, callback)
  },

  delete (id: number, callback: ResponseCallback<ItemData>) {
    const url = routeUtil.replaceParams(routes.delete, { id })
    api.delete(url, {}, callback)
  },

  create (payload: ItemCreateRequest, callback: ResponseCallback<ItemData>) {
    api.post(routes.create, payload, callback)
  },

  update (
    id: number,
    payload: ItemUpdateRequest,
    callback: ResponseCallback<ItemData>
  ) {
    const url = routeUtil.replaceParams(routes.update, { id })
    api.patch(url, payload, callback)
  },

  materialsDelta (
    id: number,
    caseQuantity: number,
    callback: ResponseCallback<MaterialDeltaData[]>
  ) {
    const url = routeUtil.replaceParams(routes.materialsDelta, { id })
    const params = { caseQuantity }
    api.get(url, params, callback)
  }
}
